import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminApi from '../../api/client';

type ProposalKind = 'author' | 'publisher';

type Proposal = {
  id: string;
  kind: ProposalKind;
  name: string | null;
  secondary_name: string | null;
  submitted_by: string;
  created_at: string | null;
};

type ProposalPage = {
  total: number;
  page: number;
  per_page: number;
  items: Proposal[];
};

const PER_PAGE = 20;

export default function UserProposalsPanel() {
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<ProposalKind>(() => searchParams.get('kind') === 'publisher' ? 'publisher' : 'author');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ProposalPage>({ total: 0, page: 1, per_page: PER_PAGE, items: [] });
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const requestedKind = searchParams.get('kind');
    if (requestedKind === 'author' || requestedKind === 'publisher') {
      setKind(requestedKind);
      setPage(1);
    }
  }, [searchParams]);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<ProposalPage>(
        `/admin/master/proposals?kind=${kind}&page=${page}&per_page=${PER_PAGE}`
      );
      setData(response.data);
      if (response.data.items.length === 0 && page > 1) setPage(page - 1);
    } catch (requestError: any) {
      setError(requestError.response?.data?.detail || 'Failed to load user proposals');
    } finally {
      setLoading(false);
    }
  }, [kind, page]);

  useEffect(() => { void fetchProposals(); }, [fetchProposals]);

  const review = async (proposal: Proposal, status: 'approved' | 'rejected') => {
    const decision = status === 'approved' ? 'approve' : 'reject';
    const effect = status === 'approved'
      ? 'This name will become available in app suggestions.'
      : 'This name will be hidden from app suggestions.';
    if (!window.confirm(`${decision === 'approve' ? 'Approve' : 'Reject'} “${proposal.name || proposal.secondary_name || 'this name'}”? ${effect}`)) return;

    setActingOn(proposal.id);
    setError(null);
    setNotice(null);
    try {
      await adminApi.post(`/admin/master/proposals/${kind}/${proposal.id}/review`, { status });
      setNotice(`${kind === 'author' ? 'Author' : 'Publisher'} proposal ${status}.`);
      await fetchProposals();
    } catch (requestError: any) {
      setError(requestError.response?.data?.detail || `Could not ${decision} this proposal`);
    } finally {
      setActingOn(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PER_PAGE));

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-5 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">User-submitted catalogue names</h2>
          <p className="text-sm text-gray-500 mt-1">Review proposed names here. Author account claims are reviewed separately.</p>
        </div>
        <button type="button" onClick={() => void fetchProposals()} disabled={loading} className="px-3 py-2 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          Refresh
        </button>
      </div>

      <div className="px-5 pt-4 flex gap-2" role="tablist" aria-label="Proposal type">
        {(['author', 'publisher'] as const).map(option => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={kind === option}
            onClick={() => {
              setKind(option);
              setPage(1);
              setData({ total: 0, page: 1, per_page: PER_PAGE, items: [] });
              setNotice(null);
            }}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${kind === option ? 'bg-[#085041] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {option === 'author' ? 'Authors' : 'Publishers'}
          </button>
        ))}
      </div>

      {error && <div role="alert" className="mx-5 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>}
      {notice && <div role="status" className="mx-5 mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded">{notice}</div>}

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-sm border-y border-gray-200">
              <th scope="col" className="p-4 font-semibold">Proposed name</th>
              <th scope="col" className="p-4 font-semibold">Submitted by</th>
              <th scope="col" className="p-4 font-semibold">Submitted</th>
              <th scope="col" className="p-4 font-semibold text-right">Review</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && data.items.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-500">Loading proposals…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-gray-500">No pending {kind} proposals.</td></tr>
            ) : data.items.map(proposal => (
              <tr key={proposal.id} className="hover:bg-gray-50">
                <td className="p-4">
                  <div className="font-medium text-gray-900">{proposal.name || '—'}</div>
                  {proposal.secondary_name && <div className="text-sm text-gray-500">{proposal.secondary_name}</div>}
                </td>
                <td className="p-4 text-gray-700">{proposal.submitted_by}</td>
                <td className="p-4 text-sm text-gray-500">{proposal.created_at ? new Date(proposal.created_at).toLocaleString() : '—'}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button type="button" disabled={loading || actingOn !== null} onClick={() => void review(proposal, 'approved')} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                    {actingOn === proposal.id ? 'Saving…' : 'Approve'}
                  </button>
                  <button type="button" disabled={loading || actingOn !== null} onClick={() => void review(proposal, 'rejected')} className="ml-2 px-3 py-1.5 rounded border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50">
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
        <span>Pending: {data.total}</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage(value => value - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage(value => value + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      </div>
    </section>
  );
}
